import { useEffect, useState } from "react";
import { Users, ShoppingCart, Clock, CheckCircle2, Wallet, DollarSign, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  const cards = [
    { icon: Users, label: "Total Customers", value: stats.total_customers, sub: `+${stats.new_customers} this week`, color: "text-blue-400" },
    { icon: ShoppingCart, label: "Total Orders", value: stats.total_orders, sub: `${stats.pending_orders} pending`, color: "text-amber-400" },
    { icon: CheckCircle2, label: "Completed Orders", value: stats.completed_orders, color: "text-emerald-400" },
    { icon: Clock, label: "Pending Payments", value: stats.pending_payments, color: "text-slate-300" },
    { icon: Wallet, label: "Confirmed Payments", value: stats.confirmed_payments, color: "text-emerald-400" },
    { icon: DollarSign, label: "Revenue (USDT)", value: stats.revenue, color: "text-amber-400" },
  ];

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-extrabold text-white">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="card-lux p-5" data-testid={`admin-stat-${i}`}>
            <c.icon className={`${c.color} mb-3`} size={22} />
            <div className="font-display text-3xl font-extrabold text-white">{c.value}</div>
            <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            {c.sub && <div className="text-xs text-amber-400/80 mt-1">{c.sub}</div>}
          </div>
        ))}
      </div>

      <div className="card-lux p-6">
        <h2 className="font-semibold text-white mb-5">Revenue — Last 7 Days (USDT)</h2>
        <div className="h-64" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.revenue_chart}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" stroke="#475569" fontSize={12} />
              <YAxis stroke="#475569" fontSize={12} />
              <Tooltip contentStyle={{ background: "#181A20", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 10, color: "#fff" }} />
              <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6">
          <h2 className="font-semibold text-white mb-4">Recent Orders</h2>
          <div className="space-y-3">
            {stats.recent_orders.length === 0 && <p className="text-slate-500 text-sm">No orders yet.</p>}
            {stats.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <div><div className="text-white">{o.service_name}</div><div className="text-xs text-slate-500 font-mono">{o.order_id}</div></div>
                <StatusBadge status={o.status} />
              </div>
            ))}
          </div>
        </div>
        <div className="card-lux p-6">
          <h2 className="font-semibold text-white mb-4">Recent Registrations</h2>
          <div className="space-y-3">
            {stats.recent_customers.length === 0 && <p className="text-slate-500 text-sm">No customers yet.</p>}
            {stats.recent_customers.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <div><div className="text-white">{c.name}</div><div className="text-xs text-slate-500">{c.email}</div></div>
                <span className="text-xs text-slate-600">{(c.created_at || "").slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
